import type {
  DisruptionCaseRow,
  DisruptionDetail,
  DisruptionGroupFacts,
  DisruptionProgress,
  ITransferFacts,
  ResolutionOp,
  ResolutionState,
  ResolutionStep,
} from '@/lib/disruptions/types';
import type { FlightFacts, PerFlightFacts } from './tower-per-flight';
import { towerT, type TowerT } from './tower-i18n';
import { formatFlightClock24, formatGroundTime } from '@/lib/disruptions/airport-tz';

// Copy for the reason / vendor codes lives in the `tower` bundle — this module
// holds KEYS, never language. An unknown code falls back to the code itself.
const noOptionsReason = (code: string, t: TowerT): string =>
  t(`reason.noOptions.${code}`, { defaultValue: code });
const vendorLabel = (code: string, t: TowerT): string =>
  t(`reason.vendor.${code}`, { defaultValue: code });

// TOWER — pure derivations for the disruption control room (no React, no IO).
//
// ONE source of numbers: `resolution-state@v1` (kpis.groups facts + steps +
// problems + implications + nextStep). Everything the Torre draws — instruments,
// the river's gates and pools, the NOW card, the ATTENTION queues — is derived
// HERE from that aggregate (plus the live progress snapshot for the run), so the
// same fact can never disagree with itself across regions. Units are PNR GROUPS
// everywhere (pax only as a secondary label). UNKNOWN facts are surfaced as
// `known=false` and drawn hatched with “waiting” — never invented, never zero.

export type PoolTone = 'pend' | 'warn' | 'danger' | 'info' | 'hatch' | 'byp' | 'ok' | 'solid';
export type LedTone = 'ok' | 'run' | 'warn' | 'info' | 'muted' | 'unknown';
export type Fill = 'solid' | 'hatch' | 'pend' | 'byp';

export interface RiverPool {
  key: string;
  n: number;
  label: string;
  tone: PoolTone;
  /** Where the pool drills to (a Tower view + a hint the roster can pre-filter on). */
  target: TowerTarget | null;
}

export type GateIndex = 0 | 1 | 2 | 3 | 4 | 5;

export interface RiverGate {
  op: ResolutionOp;
  index: GateIndex;
  name: string;
  /** Groups arriving at the gate (the flow that must pass it). */
  inFlow: number;
  /** Groups that passed the gate and keep flowing right. */
  outFlow: number;
  /** Groups that go AROUND this gate legitimately (drawn as a grey band). */
  bypass: number;
  /** Gate 0 only: groups the run already EVALUATED (assigned + options ready + no
   *  options) — the entrance ribbon colours this part 'processed' vs 'pending'. */
  processed?: number | undefined;
  bypassLabel?: string | undefined;
  /** Second line under the bypass label (what the grey band means). */
  bypassSub?: string | undefined;
  /** How the out-flow is drawn: reversible (hatch) vs committed (solid); split when mixed. */
  outFill: Fill;
  /** Sub-band of the out-flow that is already committed (solid) when outFill is 'hatch'. */
  outSolid?: number | undefined;
  led: LedTone;
  headerCount: string;
  headerSub?: string | undefined;
  /** Optional third header line (e.g. the vouchers sub-lane under Lodging). */
  headerSub2?: string | undefined;
  /** Passengers behind `outFlow` when known (per-flight fold or the facts) — the
   *  chips say “188 groups · 412 pax”, never groups alone. */
  outPax?: number | undefined;
  pools: RiverPool[];
  known: boolean;
}

export interface RiverEnd {
  travelled: number;
  ready: number;
  readyLabel: string;
  contained: boolean;
  containedAt?: string | undefined;
}

/**
 * One flight's slice of the river: its share of the entrance lane and how many
 * of ITS groups have passed each gate (exact when the per-flight fold has run,
 * pax-proportional for the entrance until then). Stacked in strip order so a
 * flight's slice sits at the same offset at every gate.
 */
export interface RiverBand {
  key: string;
  caseUrn: string;
  flightUrn: string | null;
  code: string;
  /** Groups entering (exact) — or a pax-proportional estimate (`approx`). */
  groups: number;
  approx: boolean;
  /** Per-gate pass counts for the highlight: [assigned, offer ready, accepted, issued, transferred]. */
  passed: PassTuple;
  /** Passengers in the flight (exact from the strips). */
  pax: number;
  /** Passengers behind each pass count (exact with the fold, else 0s). */
  passedPax: PassTuple;
  /* The flight card drawn INSIDE the entrance lane (replaces the strips column). */
  route: string;
  time: string;
  departed: boolean;
  status: string;
  /** The flight's hotel spend at the airline's PRICE (`airline-money@v2`); null = not available. */
  priceMinor?: number | null | undefined;
  /** Groups of this flight with a flight assigned (exact with the fold). */
  assigned?: number | undefined;
  /** Groups of this flight with no options (exact with the fold). */
  noOptions?: number | undefined;
}

export interface RiverModel {
  total: number;
  /** Passengers entering the river (kpis.affectedPassengers). */
  totalPax: number;
  gates: RiverGate[];
  end: RiverEnd;
  /** Gate index whose entering ribbon carries the "traffic" dash (a run in flight), or -1. */
  runningGate: number;
  known: boolean;
  /** Per-flight slices of the entrance lane (and their pass counts), in strip order. */
  bands: RiverBand[];
}

/** Pass counts per gate 0..4 (Departure has no pass count — travelled is the end pool). */
export type PassTuple = [number, number, number, number, number];
export const ZERO_PASS: PassTuple = [0, 0, 0, 0, 0];

export type TowerView = 'board' | 'passengers' | 'hotels' | 'cost';

export interface TowerTarget {
  view: TowerView;
  hint?: string | undefined;
  /** Hotels view: focus one hotel (`?hotel=`) and/or a transfer lens (`?transfer=`). */
  hotel?: string | undefined;
  transfer?: TransferLens | undefined;
}

/** What the Hotels view should foreground when opened from a transfer pool / row. */
export type TransferLens = 'PLAN' | 'WAITING' | 'EN_ROUTE' | 'NO_SHOW' | 'FAILED' | 'RETURN';

/* ── facts ─────────────────────────────────────────────────────────────────── */

/**
 * Facts WITH the live rebooking run folded in. The aggregate's `groups` come from
 * the cases denorm and lag the run (a group is `assigned` there only once its
 * selection is projected); the WS progress snapshot carries the run's own
 * per-group outcome counters (POL-13: assignedGroups / groupsWithOptions /
 * noOptionGroups) that move group by group. Taking the max of both keeps every
 * gate monotonic and makes the river DRAIN while the run is in flight instead of
 * jumping at the end. Only gate 0 numbers are affected.
 */
export function liveFacts(
  state: ResolutionState,
  live: DisruptionProgress | undefined,
): DisruptionGroupFacts | null {
  const g = factsOf(state);
  if (!g || !live) return g;
  // While a flight-search run is IN FLIGHT the run is the truth for gate 0: the
  // river drains to the run's own counters when it starts and refills group by
  // group (a re-run visibly starts over). Once terminal, the facts take over —
  // max() only bridges the push between 'run finished' and 'denorm caught up'.
  const searching = live.activeRun?.op === 'rebooking' && live.runStatus === 'RUNNING';
  const assigned = searching
    ? (live.assignedGroups ?? 0)
    : Math.max(g.assigned, live.assignedGroups ?? 0);
  const noOptions = searching
    ? (live.noOptionGroups ?? 0)
    : Math.max(g.noOptions, live.noOptionGroups ?? 0);
  const optionsReady = searching
    ? (live.groupsWithOptions ?? 0)
    : Math.max(g.optionsReady, live.groupsWithOptions ?? 0);
  // Hotel allocation: booking's run marker counts holds group by group while the
  // facts (booking denorm) land later — same fold for gate 2.
  const alloc = live.allocationRun;
  const heldLive = alloc ? alloc.heldGroups + alloc.alreadyReservedGroups : 0;
  const coveredFacts = g.hotelHeld + g.hotelConfirmed;
  const hotelHeld =
    heldLive > coveredFacts ? Math.max(g.hotelHeld, heldLive - g.hotelConfirmed) : g.hotelHeld;
  if (
    assigned === g.assigned &&
    noOptions === g.noOptions &&
    optionsReady === g.optionsReady &&
    hotelHeld === g.hotelHeld
  )
    return g;
  // Never exceed what can be rebooked / what needs a hotel (the run counters and
  // the facts can be out of phase by a push).
  const cap = Math.max(0, g.rebookable);
  const a = Math.min(assigned, cap);
  const n = Math.min(noOptions, Math.max(0, cap - a));
  const o = Math.min(optionsReady, Math.max(0, cap - a - n));
  const h = Math.min(hotelHeld, Math.max(0, g.hotelNeeded - g.hotelConfirmed));
  return {
    ...g,
    assigned: a,
    noOptions: n,
    optionsReady: o,
    open: Math.max(0, cap - a),
    hotelHeld: h,
  };
}

/** The run in flight. The WS-fed progress snapshot is authoritative once it exists —
 *  its `activeRun: null` after a terminal push must WIN over the aggregate's older
 *  copy (a `??` chain would resurrect a finished run from stale resolution-state). */
export function activeRunOf(
  state: ResolutionState,
  live: DisruptionProgress | undefined,
): {
  op: string;
  runId: string;
  operatorUrn: string;
  startedAt: string;
  progress?: { done: number; total: number; unit: string } | undefined;
} | null {
  if (live) return live.activeRun ?? null;
  return state.progress.activeRun ?? null;
}

export function factsOf(state: ResolutionState | undefined): DisruptionGroupFacts | null {
  const g = state?.kpis.groups;
  if (!g || !g.known?.cases) return null;
  return g;
}

/** The acceptance-bundle numbers of a facts snapshot. */
export interface BundleFacts {
  /** Groups that overnight and already hold a room (HELD or CONFIRMED). */
  hotelCovered: number;
  /** Assigned groups that need no hotel — they go AROUND Lodging to the offer. */
  flightOnly: number;
  /** Every reversible part exists (seat on HOLD; hotel HOLD when needed). */
  offerReady: number;
  /** The offer went out (== atGate). */
  offered: number;
  accepted: number;
  /** Ticket issued AND hotel confirmed — automatic after acceptance. */
  issued: number;
  /** Accepted, commit still in flight. */
  issuing: number;
  /** Commit stale beyond the BFF threshold — needs a repair. */
  commitFailed: number;
  /** Offer-ready groups whose offer has not gone out yet. */
  notSent: number;
  /** True when the BFF sent the bundle facts (false = derived from the legacy facts). */
  fromBff: boolean;
}

/**
 * ONE reading of the bundle facts. The BFF sends them explicitly (resolution-
 * state@v1 additive fields); against an older BFF they are DERIVED from the
 * legacy facts so the river still draws: flight-only = assigned groups that need
 * no hotel; offer ready = flight-only + hotel covered; issued = ticketed AND
 * accepted (the old flow issued tickets before acceptance).
 */
export function bundleOf(g: DisruptionGroupFacts): BundleFacts {
  const hotelCovered = Math.min(g.hotelNeeded, g.hotelHeld + g.hotelConfirmed);
  const undecided = Math.min(g.hotelUndecided, Math.max(0, g.assigned - g.hotelNeeded));
  const flightOnly = g.flightOnly ?? Math.max(0, g.assigned - g.hotelNeeded - undecided);
  const offerReady = Math.min(g.assigned, g.offerReady ?? flightOnly + hotelCovered);
  const offered = g.offered ?? g.atGate;
  const accepted = g.accepted;
  const issued = Math.min(accepted, g.issued ?? Math.min(g.ticketed, accepted));
  const issuing = g.acceptedNotIssued ?? Math.max(0, accepted - issued);
  const commitFailed = Math.min(issuing, g.commitFailed ?? 0);
  const notSent = Math.max(0, offerReady - offered);
  return {
    hotelCovered,
    flightOnly,
    offerReady,
    offered,
    accepted,
    issued,
    issuing,
    commitFailed,
    notSent,
    fromBff: g.offerReady != null,
  };
}

// The six gates: the passenger accepts ONE offer (flight + hotel when overnight,
// or flight only); the ticket + the hotel are then ISSUED automatically; the
// issued groups that hold a hotel are MOVED there by shuttle (MASS ground
// transport) and brought back for departure. Rebooking → Lodging → Acceptance →
// Issue (auto) → Transfers → Departure. Groups that need no hotel go AROUND
// Lodging (rejoin at the offer) and AROUND Transfers (straight to departure).
export const GATE_NAME_KEYS: Record<GateIndex, string> = {
  0: 'gate.name.rebooking',
  1: 'gate.name.lodging',
  2: 'gate.name.acceptance',
  3: 'gate.name.issue',
  4: 'gate.name.transfers',
  5: 'gate.name.departure',
};
export function gateName(i: GateIndex, t: TowerT = towerT): string {
  return t(GATE_NAME_KEYS[i]);
}
export const GATE_OPS: Record<GateIndex, ResolutionOp> = {
  0: 'rebooking',
  1: 'hotels',
  2: 'acceptance',
  3: 'tickets',
  4: 'transfers',
  5: 'contain',
};
export const GATE_INDICES: readonly GateIndex[] = [0, 1, 2, 3, 4, 5];

export function stepOf(state: ResolutionState, op: ResolutionOp): ResolutionStep | undefined {
  return state.steps.find((s) => s.op === op);
}

function ledFor(
  step: ResolutionStep | undefined,
  running: boolean,
  hasWarnPools: boolean,
): LedTone {
  if (running) return 'run';
  if (!step) return 'muted';
  switch (step.status) {
    case 'DONE':
      return 'ok';
    case 'RUNNING':
      return 'run';
    case 'PARTIAL':
      return hasWarnPools ? 'warn' : 'info';
    case 'UNKNOWN':
      return 'unknown';
    case 'SKIPPED':
      return 'muted';
    default:
      return 'muted';
  }
}

/** Maps a resolution op being fired to the run op the river/console vocabulary uses. */
export function pendingOpAsRun(op: ResolutionOp | null): string | null {
  if (!op) return null;
  if (op === 'hotels') return 'allocation';
  if (op === 'contain') return null;
  return op;
}

/** Which gate a live run feeds (the dash rides the ribbon INTO that gate). */
export function runningGateFor(op: string | undefined | null): number {
  switch (op) {
    case 'rebooking':
      return 0;
    case 'allocation':
    case 'vouchers':
      return 1;
    case 'acceptance':
      return 2;
    case 'tickets':
    case 'issue':
      return 3;
    case 'transfers':
      return 4;
    default:
      return -1;
  }
}

/* ── transfers (MASS ground transport) ─────────────────────────────────────── */

/**
 * The transfer facts as the Tower reads them: `resolution-state.kpis.transfers`
 * (the plan's counters) with the live `progress.transferRun` folded in (it moves
 * on every run / boarding push, ahead of the aggregate re-read). `null` = the BFF
 * does not speak transfers at all (older BFF) — the gate then draws as pass-through.
 *
 * Units (see `ITransferFacts`): the plan counts PASSENGERS (seats), runs are
 * VEHICLES. The river's unit is PNR groups, so seat counters are converted
 * pro-rata over the demand (`groupsOf`, groups issued with a hotel ÷ pax needed)
 * — exact once the per-flight fold has run (roster row `transfer`), proportional
 * until then. Nothing is invented: no demand → no groups.
 */
export interface TransferFacts extends ITransferFacts {
  /** Passengers to move / already at their hotel (live snapshot when present). */
  paxToMove: number;
  paxArrived: number;
  /** Seats on a dispatched run that has not arrived (boarding / on the way). */
  paxOnRoad: number;
  /** Passengers not on any dispatched run yet (incl. the not-enrolled). */
  waiting: number;
  /** ISO — the next pickup across hotels (live snapshot). */
  nextPickupAt?: string | undefined;
  /** A plan exists (something enrolled / a vehicle planned or moving). */
  hasPlan: boolean;
  /** Vehicles cut for the outbound leg, whatever their state (planned + on the road + arrived). */
  vehiclesTotal: number;
  /** Seat counter → PNR groups (river unit), given the group demand behind `needed`. */
  groupsOf: (seats: number, groupsNeeded: number) => number;
}

const EMPTY_TRANSFER_FACTS: ITransferFacts = {
  needed: 0,
  enrolled: 0,
  planned: 0,
  dispatched: 0,
  enRoute: 0,
  arrived: 0,
  noShow: 0,
  ownMeans: 0,
  failed: 0,
  hotels: 0,
  vehiclesPlanned: 0,
  vehiclesOnRoad: 0,
  returnScheduled: 0,
  returnOnRoad: 0,
  returnDone: 0,
  known: false,
};

export function transfersOf(
  state: ResolutionState,
  live: DisruptionProgress | undefined,
): TransferFacts | null {
  const t = state.kpis.transfers;
  const run = live?.transferRun ?? null;
  if (!t && !run) return null;
  const base: ITransferFacts = t ?? EMPTY_TRANSFER_FACTS;
  // The live snapshot leads the aggregate (it is bumped by every run push).
  const vehiclesOnRoad = run ? run.vehiclesOnRoad : base.vehiclesOnRoad;
  const vehiclesPlanned = run ? run.vehiclesPlanned : base.vehiclesPlanned;
  const arrivedRuns = run?.arrivedRuns ?? 0;
  const hotels = Math.max(base.hotels, run?.byHotel.length ?? 0);
  const paxArrived = Math.max(base.arrived, run?.paxArrived ?? 0);
  const paxToMove = Math.max(base.needed, base.enrolled, run?.paxTotal ?? 0, paxArrived);
  const paxOnRoad = Math.max(base.enRoute, base.dispatched - paxArrived - base.noShow, 0);
  const waiting = Math.max(0, paxToMove - paxArrived - paxOnRoad - base.noShow - base.ownMeans);
  const hasPlan =
    base.enrolled > 0 || vehiclesPlanned > 0 || vehiclesOnRoad > 0 || (run?.byHotel.length ?? 0) > 0;
  const nextPickupAt = run?.byHotel
    .map((h) => h.nextPickupAt)
    .filter((x): x is string => !!x)
    .sort()[0];
  const groupsOf = (seats: number, groupsNeeded: number): number => {
    if (groupsNeeded <= 0 || paxToMove <= 0) return 0;
    return Math.min(groupsNeeded, Math.round((seats / paxToMove) * groupsNeeded));
  };
  return {
    ...base,
    arrived: paxArrived,
    vehiclesPlanned,
    vehiclesOnRoad,
    hotels,
    known: base.known || !!run,
    paxToMove,
    paxArrived,
    paxOnRoad,
    waiting,
    nextPickupAt,
    hasPlan,
    vehiclesTotal: vehiclesPlanned + vehiclesOnRoad + arrivedRuns,
    groupsOf,
  };
}

/**
 * Format a ground operation time for display.
 *
 * Issue #662: Ground operations (shuttle pickups, check-ins, arrivals) should
 * display in the disruption airport's timezone, not the browser's timezone.
 * Pass the airportIata to format correctly; without it, falls back to UTC.
 */
export function shortTime(iso: string | undefined, airportIata?: string | null): string {
  return formatGroundTime(iso, airportIata);
}

function noOptionsSub(
  byReason: Record<string, number> | undefined,
  t: TowerT,
): string | undefined {
  if (!byReason) return undefined;
  const parts = Object.entries(byReason)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([k, n]) => `${noOptionsReason(k, t).toLowerCase()} ${n}`);
  return parts.length ? parts.join(' · ') : undefined;
}

/* ── the river ─────────────────────────────────────────────────────────────── */

/** Per-flight slices of the entrance lane, in strip order (see RiverBand). */
export function buildBands(strips: FlightStrip[], g: DisruptionGroupFacts | null): RiverBand[] {
  if (!strips.length) return [];
  const exact = strips.every((s) => s.facts != null);
  const totalGroups = g ? Math.max(1, g.groups + g.excluded) : 0;
  const totalPax = Math.max(
    1,
    strips.reduce((a, s) => a + s.pax, 0),
  );
  return strips
    .filter((s) => s.pax > 0 || (s.facts?.groups ?? 0) > 0)
    .map((s) => {
      const f = s.facts;
      const groups = f ? f.groups : totalGroups > 0 ? (s.pax / totalPax) * totalGroups : 0;
      return {
        key: s.caseUrn,
        caseUrn: s.caseUrn,
        flightUrn: s.flightUrn ?? null,
        code: s.code,
        groups,
        approx: !exact,
        passed: f
          ? [f.assigned, f.offerReady, f.accepted, f.issued, f.transferDone ?? 0]
          : ZERO_PASS,
        pax: s.pax,
        passedPax: f
          ? [f.paxAssigned, f.paxOfferReady, f.paxAccepted, f.paxIssued, f.paxTransferred ?? 0]
          : ZERO_PASS,
        route: s.route,
        time: s.time,
        departed: s.departed,
        status: s.status,
        priceMinor: s.priceMinor,
        assigned: f?.assigned,
        noOptions: f?.noOptions,
      };
    });
}

export function buildRiver(
  state: ResolutionState,
  live: DisruptionProgress | undefined,
  detail: DisruptionDetail | undefined,
  pendingOp: ResolutionOp | null = null,
  strips: FlightStrip[] = [],
  t: TowerT = towerT,
): RiverModel {
  const g = liveFacts(state, live);
  const bands = buildBands(strips, g);
  // A fire whose POST is still in flight (tickets / vouchers / acceptance are
  // synchronous fan-outs — the BFF answers when the airline did) counts as running
  // for the picture: the dash rides into that gate from the click, not from the
  // first WS push.
  const activeOp = activeRunOf(state, live)?.op ?? pendingOpAsRun(pendingOp);
  const runningGate = runningGateFor(activeOp);
  const containStep = stepOf(state, 'contain');
  const contained =
    containStep?.status === 'DONE' ||
    detail?.status === 'CONTAINED' ||
    detail?.status === 'RESOLVED';

  if (!g) {
    // Facts unreadable → every gate hatched “waiting”. Never zeros.
    const gates: RiverGate[] = GATE_INDICES.map((i) => ({
      op: GATE_OPS[i],
      index: i,
      name: gateName(i, t),
      inFlow: 0,
      outFlow: 0,
      bypass: 0,
      outFill: 'hatch',
      led: 'unknown',
      headerCount: t('gate.unknown.count'),
      headerSub: i === 0 ? t('gate.unknown.sub') : undefined,
      pools: [],
      known: false,
    }));
    return {
      total: state.kpis.affectedPassengers > 0 ? 1 : 0,
      totalPax: state.kpis.affectedPassengers,
      gates,
      end: {
        travelled: 0,
        ready: 0,
        readyLabel: t('end.ready'),
        contained,
        containedAt: detail?.containedAt,
      },
      runningGate,
      known: false,
      bands,
    };
  }

  const p = state.problems;
  const total = Math.max(1, g.groups + g.excluded);
  // Passengers behind each gate's out-flow: exact from the per-flight fold when
  // every strip has it, else the facts' assignedPax for gate 0 and unknown after.
  const exactPax = bands.length > 0 && bands.every((x) => !x.approx);
  const paxAt = (i: 0 | 1 | 2 | 3 | 4): number | undefined =>
    exactPax ? bands.reduce((a, x) => a + x.passedPax[i], 0) : i === 0 && g.assignedPax > 0 ? g.assignedPax : undefined;
  const noOptions = g.noOptions;
  const optionsReady = g.optionsReady;
  const pending = Math.max(0, g.rebookable - g.assigned - noOptions - optionsReady);

  // Gate 0 · Rebooking
  const s0 = stepOf(state, 'rebooking');
  const pools0: RiverPool[] = [];
  if (pending > 0)
    pools0.push({
      key: 'pending',
      n: pending,
      label: t('gate.rebooking.pool.pending'),
      tone: 'pend',
      target: { view: 'passengers', hint: 'PENDING' },
    });
  if (optionsReady > 0)
    pools0.push({
      key: 'options',
      n: optionsReady,
      label: t('gate.rebooking.pool.optionsReady'),
      tone: 'info',
      target: { view: 'passengers', hint: 'OPTIONS_READY' },
    });
  if (noOptions > 0) {
    const sub = noOptionsSub(p.noOptionsByReason, t);
    pools0.push({
      key: 'noopt',
      n: noOptions,
      label: sub
        ? t('gate.rebooking.pool.noOptionsWithReasons', { reasons: sub })
        : t('gate.rebooking.pool.noOptions'),
      tone: 'warn',
      target: { view: 'passengers', hint: 'NO_OPTIONS' },
    });
  }
  if (g.notApplicable > 0)
    pools0.push({
      key: 'na',
      n: g.notApplicable,
      label: t('gate.rebooking.pool.notApplicable'),
      tone: 'byp',
      target: { view: 'passengers', hint: 'NOT_APPLICABLE' },
    });
  if (g.excluded > 0)
    pools0.push({
      key: 'excl',
      n: g.excluded,
      label: t('gate.rebooking.pool.excluded'),
      tone: 'byp',
      target: { view: 'passengers' },
    });
  const gate0: RiverGate = {
    op: 'rebooking',
    index: 0,
    name: gateName(0, t),
    inFlow: g.rebookable + g.notApplicable + g.excluded,
    outFlow: g.assigned,
    bypass: 0,
    processed: g.assigned + optionsReady + noOptions,
    // Seats are reversible (HOLD) until the ticket is issued — the issued part of
    // the ribbon reads solid from the first gate on.
    outFill: 'hatch',
    outSolid: Math.min(g.assigned, g.ticketed),
    led: ledFor(s0, runningGate === 0, noOptions > 0),
    headerCount: `${g.assigned}/${g.rebookable}`,
    headerSub:
      runningGate === 0
        ? t('gate.rebooking.sub.running')
        : pending + noOptions > 0
          ? t('gate.rebooking.sub.withoutFlight', { count: pending + noOptions })
          : g.ticketed >= g.assigned && g.assigned > 0
            ? t('gate.rebooking.sub.ticketed')
            : t('gate.rebooking.sub.reversible'),
    pools: pools0,
    outPax: paxAt(0),
    known: true,
  };

  // Gate 1 · Lodging (hotels; vouchers as a sub-lane). Groups with a flight that
  // overnight must hold a room before the offer can go out; the rest go AROUND
  // this gate (grey band) straight to the offer.
  const b = bundleOf(g);
  const s1 = stepOf(state, 'hotels');
  const need = g.hotelNeeded;
  const covered = b.hotelCovered;
  const undecided = Math.min(g.hotelUndecided, Math.max(0, g.assigned - need));
  const bypass1 = b.flightOnly;
  const pools1: RiverPool[] = [];
  const alloc = live?.allocationRun;
  const gaps = Math.max(p.allocationGaps, alloc ? alloc.soldOutGroups + alloc.noOfferGroups : 0);
  // The JAR to empty into hotels: the overnight verdict is known right after the
  // flight search, so the groups that still need a hotel pool under the gate from
  // that moment — and drain into HOLD as the booking runs. Same for vouchers due.
  const needOpen = Math.max(0, need - covered - gaps);
  // Legacy alarm: a ticket issued (repair route / old flow) while the passenger has
  // no room is not "pending", it is an alarm.
  const ticketedNoHotel = g.ticketed > 0 && needOpen > 0 && g.ticketed > b.issued;
  if (needOpen > 0)
    pools1.push({
      key: 'need',
      n: needOpen,
      label: ticketedNoHotel
        ? t('gate.lodging.pool.needTicketed')
        : t('gate.lodging.pool.need'),
      tone: ticketedNoHotel ? 'warn' : 'pend',
      target: { view: 'hotels' },
    });
  const vouchersDue = Math.max(0, g.voucherEligible - g.voucherIssued);
  if (vouchersDue > 0)
    pools1.push({
      key: 'vouchers',
      n: vouchersDue,
      label: t('gate.lodging.pool.vouchers'),
      tone: 'info',
      target: { view: 'passengers' },
    });
  // Hotel gaps only mean something once the flow has reached the gate.
  if (g.assigned > 0 && gaps > 0)
    pools1.push({
      key: 'gap',
      n: gaps,
      label: t('gate.lodging.pool.gap'),
      tone: 'warn',
      target: { view: 'hotels' },
    });
  // “Undecided” only for groups that reached the gate (the fact also counts the
  // not-yet-assigned, which are the Rebooking pool, not a hotel gap).
  if (g.assigned > 0 && undecided > 0)
    pools1.push({
      key: 'undecided',
      n: undecided,
      label: t('gate.lodging.pool.undecided'),
      tone: 'pend',
      target: { view: 'hotels' },
    });
  if (g.assigned > 0 && g.voucherNoKyc > 0)
    pools1.push({
      key: 'nokyc',
      n: g.voucherNoKyc,
      label: t('gate.lodging.pool.noKyc'),
      tone: 'info',
      target: { view: 'passengers' },
    });
  const vouchers =
    g.voucherEligible > 0
      ? t('gate.lodging.sub.vouchers', {
          issued: g.voucherIssued,
          eligible: g.voucherEligible,
        })
      : '';
  const skipped1 = s1?.status === 'SKIPPED';
  const gate1: RiverGate = {
    op: 'hotels',
    index: 1,
    name: gateName(1, t),
    inFlow: need,
    outFlow: covered,
    bypass: bypass1,
    bypassLabel: bypass1 > 0 ? t('gate.lodging.bypass', { count: bypass1 }) : undefined,
    outFill: g.hotelConfirmed >= covered && covered > 0 ? 'solid' : 'hatch',
    outSolid: g.hotelConfirmed,
    led: ledFor(s1, runningGate === 1, gaps > 0 || ticketedNoHotel),
    headerCount:
      need > 0
        ? `${covered}/${need}`
        : skipped1
          ? t('gate.lodging.count.notNeeded')
          : g.assigned > 0
            ? t('gate.lodging.count.afterRebooking')
            : t('gate.lodging.count.waiting'),
    headerSub:
      need > 0
        ? runningGate === 1
          ? t('gate.lodging.sub.booking')
          : t('gate.lodging.sub.status', {
              confirmed: g.hotelConfirmed,
              held: g.hotelHeld,
            })
        : skipped1
          ? t('gate.lodging.sub.nobody')
          : undefined,
    headerSub2: vouchers,
    pools: pools1,
    outPax: paxAt(1),
    known: true,
  };

  // Gate 2 · Acceptance — the offer (flight + hotel, or flight only) goes out by
  // itself once every part exists; the passenger accepts or declines; a
  // supervisor may accept on their behalf. Silence never accepts.
  const s2 = stepOf(state, 'acceptance');
  const offerReady = b.offerReady;
  const pools2: RiverPool[] = [];
  const acceptanceOpen = s2 ? s2.status !== 'BLOCKED' && s2.status !== 'SKIPPED' : true;
  const notSentAlarm = acceptanceOpen && b.fromBff;
  if (b.notSent > 0)
    pools2.push({
      key: 'notsent',
      n: b.notSent,
      label: acceptanceOpen
        ? t('gate.acceptance.pool.notSent')
        : t('gate.acceptance.pool.waitsHotels'),
      tone: notSentAlarm ? 'warn' : 'pend',
      target: { view: 'passengers', hint: 'SELECTED' },
    });
  if (g.awaiting > 0)
    pools2.push({
      key: 'awaiting',
      n: g.awaiting,
      label: t('gate.acceptance.pool.awaiting'),
      tone: 'hatch',
      target: { view: 'passengers' },
    });
  if (g.declined > 0)
    pools2.push({
      key: 'declined',
      n: g.declined,
      label: t('gate.acceptance.pool.declined'),
      tone: 'danger',
      target: { view: 'passengers' },
    });
  const gate2: RiverGate = {
    op: 'acceptance',
    index: 2,
    name: gateName(2, t),
    inFlow: offerReady,
    outFlow: g.accepted,
    bypass: 0,
    outFill: 'solid',
    led: ledFor(s2, runningGate === 2, g.declined > 0 || (notSentAlarm && b.notSent > 0)),
    headerCount:
      offerReady > 0
        ? `${g.accepted}/${offerReady}`
        : g.assigned > 0
          ? t('gate.acceptance.count.noOffer')
          : t('gate.acceptance.count.waiting'),
    headerSub:
      offerReady > 0
        ? runningGate === 2
          ? t('gate.acceptance.sub.accepting')
          : `${t('gate.acceptance.sub.status', { awaiting: g.awaiting, declined: g.declined })}${b.notSent > 0 ? t('gate.acceptance.sub.notSent', { count: b.notSent }) : ''}`
        : g.assigned > 0
          ? t('gate.acceptance.sub.first')
          : undefined,
    pools: pools2,
    outPax: paxAt(2),
    known: true,
  };

  // Gate 3 · Issue (auto) — after acceptance the ticket is issued and the hotel
  // confirmed with no operator action. Its ribbon carries the traffic while a
  // commit is in flight; a stale commit pools as a repair.
  const s3 = stepOf(state, 'tickets');
  const pools3: RiverPool[] = [];
  const issuing = Math.max(0, b.issuing - b.commitFailed);
  if (issuing > 0)
    pools3.push({
      key: 'issuing',
      n: issuing,
      label: t('gate.issue.pool.issuing'),
      tone: 'hatch',
      target: { view: 'passengers' },
    });
  if (b.commitFailed > 0)
    pools3.push({
      key: 'failed',
      n: b.commitFailed,
      label: t('gate.issue.pool.failed'),
      tone: 'danger',
      target: { view: 'passengers' },
    });
  const gate3: RiverGate = {
    op: 'tickets',
    index: 3,
    name: gateName(3, t),
    inFlow: g.accepted,
    outFlow: b.issued,
    bypass: 0,
    outFill: 'solid',
    led:
      b.commitFailed > 0
        ? 'warn'
        : issuing > 0 || runningGate === 3
          ? 'run'
          : ledFor(s3, false, false),
    headerCount:
      g.accepted > 0 ? `${b.issued}/${g.accepted}` : t('gate.issue.count.waiting'),
    headerSub:
      g.accepted === 0
        ? t('gate.issue.sub.automatic')
        : issuing > 0
          ? t('gate.issue.sub.issuing', { count: issuing })
          : b.commitFailed > 0
            ? t('gate.issue.sub.failed', { count: b.commitFailed })
            : t('gate.issue.sub.irreversible'),
    pools: pools3,
    outPax: paxAt(3),
    known: true,
  };

  // Gate 4 · Transfers — the issued groups that hold a hotel are MOVED there by
  // shuttle (bus 45 · van 12 · car 3, cut per hotel, dispatched in waves; the
  // return run is scheduled automatically at departure − lead). Flight-only
  // issued groups go AROUND it straight to departure. Reversible until pickup.
  const tf = transfersOf(state, live);
  const s4 = stepOf(state, 'transfers');
  const wired = !!tf || !!s4;
  // In-flow: issued GROUPS that hold a hotel (issued ∩ hotel confirmed from the
  // group facts; exact per flight once the roster fold carries `transfer`). The
  // BFF's `needed` speaks passengers — it sizes the pax, not the group count.
  const foldNeeded = exactPax
    ? bands.reduce((a, x) => a + (strips.find((st) => st.caseUrn === x.caseUrn)?.facts?.transferNeeded ?? 0), 0)
    : 0;
  const hotelIssued = !wired
    ? b.issued // older BFF: everything issued passes straight through
    : Math.min(b.issued, foldNeeded > 0 ? foldNeeded : g.hotelConfirmed);
  const bypass4 = Math.max(0, b.issued - hotelIssued);
  const skipped4 = s4?.status === 'SKIPPED';
  const pools4: RiverPool[] = [];
  let out4 = 0;
  let outFill4: Fill = 'solid';
  let led4: LedTone = 'muted';
  let headerCount4 = t('gate.transfers.count.waiting');
  let headerSub4: string | undefined;
  let headerSub2_4: string | undefined;
  if (!wired) {
    // Older BFF: nothing to draw — the issued flow passes straight through so the
    // Departure picture is the classic one.
    out4 = hotelIssued;
    headerCount4 =
      hotelIssued > 0 ? t('gate.transfers.count.notWired') : t('gate.transfers.count.waiting');
    headerSub4 = hotelIssued > 0 ? t('gate.transfers.sub.notWired') : undefined;
  } else if (
    skipped4 ||
    (tf && tf.known && tf.needed === 0 && !tf.hasPlan && hotelIssued === 0)
  ) {
    headerCount4 = t('gate.transfers.count.notNeeded');
    headerSub4 = t('gate.transfers.sub.notNeeded');
    led4 = 'muted';
  } else if (!tf || !tf.known) {
    // Transport unreadable → the whole in-flow waits, hatched. Never zeros.
    if (hotelIssued > 0)
      pools4.push({
        key: 'waiting',
        n: hotelIssued,
        label: t('gate.transfers.pool.unknown'),
        tone: 'hatch',
        target: { view: 'hotels', transfer: 'WAITING' },
      });
    outFill4 = 'hatch';
    led4 = 'unknown';
    headerCount4 = t('gate.transfers.count.waiting');
    headerSub4 =
      hotelIssued > 0 ? t('gate.transfers.sub.unknown', { count: hotelIssued }) : undefined;
  } else {
    const arrivedG = tf.groupsOf(tf.paxArrived + tf.ownMeans, hotelIssued);
    out4 = Math.min(hotelIssued, arrivedG);
    const enrouteG = tf.groupsOf(tf.paxOnRoad, hotelIssued);
    const noShowG = tf.groupsOf(tf.noShow, hotelIssued);
    // `failed` counts RUNS (vehicles), not seats — a failed run pools as one unit
    // per vehicle so it is never invisible, never inflated.
    const failedG = Math.min(tf.failed, Math.max(0, hotelIssued - out4 - enrouteG - noShowG));
    // The jar under the gate: issued groups with a hotel that hold no seat on a
    // dispatched run yet (no plan → all of them wait).
    const waitingG = Math.max(0, hotelIssued - out4 - enrouteG - noShowG - failedG);
    if (waitingG > 0)
      pools4.push({
        key: 'waiting',
        n: waitingG,
        label: tf.hasPlan
          ? t('gate.transfers.pool.waiting')
          : t('gate.transfers.pool.waitingNoPlan'),
        tone: 'pend',
        target: { view: 'hotels', transfer: 'WAITING' },
      });
    if (enrouteG > 0)
      pools4.push({
        key: 'enroute',
        n: enrouteG,
        label: t('gate.transfers.pool.enRoute'),
        tone: 'hatch',
        target: { view: 'hotels', transfer: 'EN_ROUTE' },
      });
    if (noShowG > 0)
      pools4.push({
        key: 'noshow',
        n: noShowG,
        label: t('gate.transfers.pool.noShow'),
        tone: 'danger',
        target: { view: 'hotels', transfer: 'NO_SHOW' },
      });
    if (failedG > 0)
      pools4.push({
        key: 'failed',
        n: failedG,
        label: t('gate.transfers.pool.failed'),
        tone: 'warn',
        target: { view: 'hotels', transfer: 'FAILED' },
      });
    led4 =
      tf.vehiclesOnRoad > 0 || runningGate === 4
        ? 'run'
        : ledFor(s4, false, tf.noShow + tf.failed > 0);
    headerCount4 =
      hotelIssued > 0
        ? `${out4}/${hotelIssued}`
        : tf.hasPlan
          ? t('gate.transfers.count.pax', { arrived: tf.paxArrived, total: tf.paxToMove })
          : t('gate.transfers.count.waiting');
    headerSub4 =
      hotelIssued > 0 || tf.hasPlan
        ? t('gate.transfers.sub.onRoad', {
            count: tf.vehiclesOnRoad,
            waiting: tf.waiting,
          })
        : t('gate.transfers.sub.afterIssue');
    headerSub2_4 =
      tf.returnScheduled + tf.returnOnRoad + tf.returnDone > 0
        ? t('gate.transfers.sub.return', {
            scheduled: tf.returnScheduled,
            done: tf.returnDone,
          })
        : undefined;
  }
  const gate4: RiverGate = {
    op: 'transfers',
    index: 4,
    name: gateName(4, t),
    inFlow: hotelIssued,
    outFlow: out4,
    bypass: bypass4,
    bypassLabel: bypass4 > 0 ? t('gate.transfers.bypass', { count: bypass4 }) : undefined,
    bypassSub: bypass4 > 0 ? t('gate.transfers.bypassSub') : undefined,
    outFill: outFill4,
    led: led4,
    headerCount: headerCount4,
    headerSub: headerSub4,
    headerSub2: headerSub2_4,
    pools: pools4,
    outPax: tf && tf.known && tf.paxArrived > 0 ? tf.paxArrived : paxAt(4),
    known: true,
  };

  // Gate 5 · Departure (contain)
  const flowIn5 = out4 + bypass4;
  const gate5: RiverGate = {
    op: 'contain',
    index: 5,
    name: gateName(5, t),
    inFlow: flowIn5,
    // Only the groups that FLEW pass this gate — issued-but-not-departed wait in
    // the pale pool after it (never drawn as done).
    outFlow: g.travelled,
    bypass: 0,
    outFill: 'solid',
    led: contained
      ? 'ok'
      : containStep?.containMode === 'NATURAL'
        ? 'ok'
        : containStep?.containMode === 'TRAVELLING'
          ? 'info'
          : 'muted',
    headerCount:
      g.travelled > 0 || g.ticketedNotDeparted > 0
        ? t('gate.departure.count.travelled', { count: g.travelled })
        : flowIn5 > 0
          ? t('gate.departure.count.onTheWay')
          : t('gate.departure.count.waiting'),
    headerSub: contained
      ? t('gate.departure.sub.contained')
      : containStep?.containMode === 'NATURAL'
        ? t('gate.departure.sub.natural')
        : g.ticketedNotDeparted > 0
          ? t('gate.departure.sub.pending', { count: g.ticketedNotDeparted })
          : undefined,
    pools: [],
    known: true,
  };

  return {
    total,
    totalPax: state.kpis.affectedPassengers,
    gates: [gate0, gate1, gate2, gate3, gate4, gate5],
    end: {
      travelled: g.travelled,
      ready: g.ticketedNotDeparted,
      readyLabel: tf && tf.hasPlan ? t('end.readyAtHotel') : t('end.readyWaiting'),
      contained,
      containedAt: detail?.containedAt,
    },
    // The automatic commit is a run of its own: while accepted groups are being
    // issued the Issue ribbon carries the traffic even with no run marker — and
    // shuttles on the road are the same for the Transfers ribbon.
    runningGate:
      runningGate === -1 && !pendingOp
        ? issuing > 0
          ? 3
          : tf && tf.vehiclesOnRoad > 0
            ? 4
            : runningGate
        : runningGate,
    known: true,
    bands,
  };
}

/* ── instruments ───────────────────────────────────────────────────────────── */

export interface Instrument {
  key: 'groups' | 'flights' | 'hotel' | 'offers' | 'issued' | 'cost';
  label: string;
  value: string;
  small?: string | undefined;
  /** Up to three bar segments in % of the cell width: [solid, hatch, warn]. */
  bar: [number, number, number];
  hatchedValue?: boolean | undefined;
  target: TowerTarget | null;
}

/** Formats an airline amount (`airline-money@v2`): null / undefined → «No disponible». */
export type PriceFormatter = (minor: number | null | undefined, currency: string) => string;

export function buildInstruments(
  state: ResolutionState,
  detail: DisruptionDetail | undefined,
  now: number,
  formatPrice: PriceFormatter,
  live?: DisruptionProgress,
  t: TowerT = towerT,
): Instrument[] {
  const g = liveFacts(state, live);
  const k = state.kpis;
  const cases = detail?.cases ?? [];
  const departed = cases.filter(
    (c) => c.departureAt && new Date(c.departureAt).getTime() < now,
  ).length;
  const pct = (n: number, d: number) => (d > 0 ? Math.max(0, Math.min(100, (n / d) * 100)) : 0);

  // People first: the headline number is PASSENGERS (the unit everywhere else is
  // PNR groups — a group is 1..n people, so the counters alone never say how many
  // human beings the disruption touches).
  const groups: Instrument = g
    ? {
        key: 'groups',
        label: t('instruments.groups.label'),
        value: t('instruments.groups.value', { value: k.affectedPassengers.toLocaleString() }),
        small:
          g.assignedPax > 0
            ? t('instruments.groups.smallPax', {
                groups: g.groups,
                assigned: g.assigned,
                pax: g.assignedPax.toLocaleString(),
              })
            : t('instruments.groups.small', { groups: g.groups, assigned: g.assigned }),
        bar: [
          pct(bundleOf(g).issued, g.groups),
          pct(Math.max(0, g.assigned - bundleOf(g).issued), g.groups),
          pct(g.noOptions, g.groups),
        ],
        target: { view: 'passengers' },
      }
    : {
        key: 'groups',
        label: t('instruments.groups.label'),
        value:
          k.affectedPassengers > 0
            ? t('instruments.groups.value', { value: k.affectedPassengers.toLocaleString() })
            : '—',
        small: t('instruments.groups.notLoaded'),
        bar: [0, 0, 0],
        hatchedValue: true,
        target: { view: 'passengers' },
      };

  const flights: Instrument = {
    key: 'flights',
    label: t('instruments.flights.label'),
    value: String(k.caseCount || cases.length),
    small: cases.length
      ? t('instruments.flights.departed', { count: departed })
      : t('instruments.flights.none'),
    bar: [pct(departed, cases.length), 0, 0],
    target: null,
  };

  const hotelsSkipped = stepOf(state, 'hotels')?.status === 'SKIPPED';
  const tr = transfersOf(state, live);
  const hotel: Instrument = g
    ? g.hotelNeeded > 0
      ? {
          key: 'hotel',
          label: t('instruments.hotel.label'),
          value: `${g.hotelHeld + g.hotelConfirmed}`,
          // Once a transfer plan exists the second line says where the people ARE.
          small:
            tr && tr.known && tr.hasPlan
              ? t('instruments.hotel.smallTransfer', {
                  arrived: tr.paxArrived.toLocaleString(),
                  onRoad: tr.paxOnRoad.toLocaleString(),
                  needed: g.hotelNeeded,
                })
              : t('instruments.hotel.small', {
                  needed: g.hotelNeeded,
                  confirmed: g.hotelConfirmed,
                  held: g.hotelHeld,
                }),
          bar: [
            pct(g.hotelConfirmed, g.hotelNeeded),
            pct(g.hotelHeld, g.hotelNeeded),
            pct(state.problems.allocationGaps, g.hotelNeeded),
          ],
          hatchedValue: g.hotelConfirmed === 0 && g.hotelHeld > 0,
          target: { view: 'hotels' },
        }
      : {
          key: 'hotel',
          label: t('instruments.hotel.label'),
          value: '—',
          small: hotelsSkipped
            ? t('instruments.hotel.notNeeded')
            : t('instruments.hotel.afterRebooking'),
          bar: [0, 0, 0],
          target: { view: 'hotels' },
        }
    : {
        key: 'hotel',
        label: t('instruments.hotel.label'),
        value: '—',
        small: t('instruments.noData'),
        bar: [0, 0, 0],
        hatchedValue: true,
        target: { view: 'hotels' },
      };

  const b = g ? bundleOf(g) : null;
  const offers: Instrument =
    g && b
      ? b.offerReady > 0 || g.atGate > 0
        ? {
            key: 'offers',
            label: t('instruments.offers.label'),
            value: `${g.accepted}/${Math.max(b.offerReady, b.offered)}`,
            small: `${t('instruments.offers.small', { awaiting: g.awaiting, declined: g.declined })}${b.notSent > 0 ? t('instruments.offers.notSent', { count: b.notSent }) : ''}`,
            bar: [
              pct(g.accepted, Math.max(b.offerReady, b.offered)),
              pct(g.awaiting, Math.max(b.offerReady, b.offered)),
              pct(g.declined, Math.max(b.offerReady, b.offered)),
            ],
            target: { view: 'passengers' },
          }
        : {
            key: 'offers',
            label: t('instruments.offers.label'),
            value: '—',
            small:
              g.assigned > 0
                ? t('instruments.offers.first')
                : t('instruments.offers.none'),
            bar: [0, 0, 0],
            target: { view: 'passengers' },
          }
      : {
          key: 'offers',
          label: t('instruments.offers.label'),
          value: '—',
          small: t('instruments.noData'),
          bar: [0, 0, 0],
          hatchedValue: true,
          target: { view: 'passengers' },
        };

  const issued: Instrument =
    g && b
      ? g.accepted > 0
        ? {
            key: 'issued',
            label: t('instruments.issued.label'),
            value: `${b.issued}/${g.accepted}`,
            small:
              b.commitFailed > 0
                ? t('instruments.issued.failed', {
                    failed: b.commitFailed,
                    issuing: Math.max(0, b.issuing - b.commitFailed),
                  })
                : b.issuing > 0
                  ? t('instruments.issued.issuing', { count: b.issuing })
                  : t('instruments.issued.automatic'),
            bar: [
              pct(b.issued, g.accepted),
              pct(Math.max(0, b.issuing - b.commitFailed), g.accepted),
              pct(b.commitFailed, g.accepted),
            ],
            hatchedValue: b.issued === 0 && b.issuing > 0,
            target: { view: 'passengers' },
          }
        : {
            key: 'issued',
            label: t('instruments.issued.label'),
            value: '—',
            small: t('instruments.issued.afterAcceptance'),
            bar: [0, 0, 0],
            target: { view: 'passengers' },
          }
      : {
          key: 'issued',
          label: t('instruments.issued.label'),
          value: '—',
          small: t('instruments.noData'),
          bar: [0, 0, 0],
          hatchedValue: true,
          target: { view: 'passengers' },
        };

  // airline-money@v2 — the hotel spend at the airline's PRICE (converted server-side).
  // A null total is not available (no price card / only the unpriceable S4 estimate):
  // the cell says «No disponible» and draws no bar — never a 0.
  const totalPrice = k.totalPriceMinor;
  const priced = typeof totalPrice === 'number';
  const booked = k.bookedPriceMinor ?? 0;
  const held = k.heldPriceMinor ?? 0;
  const est = k.estimatedPriceMinor ?? 0;
  const priceParts: string[] = [];
  if (priced && booked > 0)
    priceParts.push(t('instruments.cost.booked', { amount: formatPrice(booked, k.currency) }));
  if (priced && held > 0) priceParts.push(t('instruments.cost.held', { amount: formatPrice(held, k.currency) }));
  if (priced && est > 0)
    priceParts.push(t('instruments.cost.estimated', { amount: formatPrice(est, k.currency) }));
  const cost: Instrument = {
    key: 'cost',
    label:
      k.priceSource === 'actual+estimate'
        ? t('instruments.cost.labelActual')
        : t('instruments.cost.labelEstimate'),
    value: hotelsSkipped && totalPrice === 0 ? '$0' : formatPrice(totalPrice, k.currency),
    small: priceParts.length
      ? priceParts.join(' · ')
      : hotelsSkipped
        ? t('instruments.cost.noHotels')
        : priced
          ? t('instruments.cost.noSpend')
          : t('instruments.cost.unavailable'),
    bar: priced ? [pct(booked, totalPrice), pct(held, totalPrice), pct(est, totalPrice)] : [0, 0, 0],
    hatchedValue: !priced || (booked === 0 && (held > 0 || est > 0)),
    target: { view: 'cost' },
  };

  return [groups, flights, hotel, offers, issued, cost];
}

/* ── NOW ─────────────────────────────────────────────────────────────────── */

// Acceptance bundle: rebook → hotels (HOLD) → [vouchers] → the offer goes out
// by itself → the passenger (or a supervisor) accepts → ticket + hotel are
// issued automatically → contain. `tickets` is the automatic step's op id.
export const STEP_ORDER: ResolutionOp[] = [
  'rebooking',
  'hotels',
  'vouchers',
  'acceptance',
  'tickets',
  'transfers',
  'contain',
];
export const COMMIT_OPS: ReadonlySet<ResolutionOp> = new Set<ResolutionOp>([
  'tickets',
  'acceptance',
  'contain',
]);

export type Reversibility = 'reversible' | 'irreversible' | 'destructive' | 'wait' | 'done';

export interface NowCard {
  op: ResolutionOp | 'none';
  eyebrow: string;
  headline: string;
  why: string;
  facts: Array<[string, string]>;
  reversibility: Reversibility;
  reversibilityLine: string;
  cta: string;
  actionable: boolean;
  requiresSupervisor: boolean;
  after: string;
  mode: 'start' | 'retry' | 'wait' | 'blocked' | 'unknown' | 'done' | 'running';
  /** An optional secondary link under the CTA (e.g. "Review plan" → hotels view). */
  secondary?: { label: string; target: TowerTarget } | undefined;
  /** QA fix 4 — the primary button fires a dedicated REPAIR instead of resolve-next:
   *  `send-offers` re-opens the acceptance gate for stuck-complete offers. */
  action?: 'send-offers' | undefined;
}

export const OP_STEP_KEY: Record<ResolutionOp, string> = {
  rebooking: 'step.rebooking',
  tickets: 'step.tickets',
  hotels: 'step.hotels',
  vouchers: 'step.vouchers',
  acceptance: 'step.acceptance',
  transfers: 'step.transfers',
  contain: 'step.contain',
};
export function opStepLabel(op: ResolutionOp, t: TowerT = towerT): string {
  return t(OP_STEP_KEY[op]);
}

/** The ordered steps THIS state speaks (an older BFF has no `transfers` step —
 *  never count or list a step the BFF does not know). */
export function stepOrderOf(state: ResolutionState): ResolutionOp[] {
  return STEP_ORDER.filter((o) => !!stepOf(state, o) || o === state.nextStep.op);
}

/** The commit step is PASSIVE (issue ticket + confirm hotel run by themselves after
 *  acceptance) when the BFF says so — or when it sends the bundle facts at all. An
 *  older BFF still gets the classic "issue tickets" button. */
export function isAutomaticStep(step: ResolutionStep | undefined, g: DisruptionGroupFacts | null): boolean {
  if (!step || step.op !== 'tickets') return false;
  if (step.automatic != null) return step.automatic;
  return g?.offerReady != null;
}

export function buildNow(
  state: ResolutionState,
  live: DisruptionProgress | undefined,
  formatPrice: PriceFormatter,
  t: TowerT = towerT,
): NowCard {
  const g = liveFacts(state, live);
  const next = state.nextStep;
  const op = next.op;
  const step = op === 'none' ? undefined : stepOf(state, op);
  const activeRun = activeRunOf(state, live);
  const running = !!activeRun;
  const contained = stepOf(state, 'contain')?.status === 'DONE';
  const order = stepOrderOf(state);
  const stepCount = Math.max(order.length, 1);
  const idx = op === 'none' ? stepCount : order.indexOf(op) + 1;
  const tf = transfersOf(state, live);
  // The count the headline speaks about: the step's own `remaining`, else the
  // facts (never a stale `total`).
  const n =
    step?.remaining ??
    (op === 'rebooking' && g ? Math.max(0, g.rebookable - g.assigned) : (step?.total ?? 0));
  const automatic = isAutomaticStep(step, g);
  const b = g ? bundleOf(g) : null;
  // Shuttles on the road are an automatic phase of their own: nothing to click
  // until the wave settles (or a no-show asks for a re-dispatch).
  const shuttlesMoving = op === 'transfers' && !!tf && tf.vehiclesOnRoad > 0 && next.mode !== 'retry';
  const mode: NowCard['mode'] = running
    ? 'running'
    : contained
      ? 'done'
      : automatic || shuttlesMoving
        ? 'wait'
        : (next.mode ?? (op === 'none' ? 'done' : 'start'));
  const remainingAfter = order.filter((o) => {
    if (op === 'none') return false;
    if (order.indexOf(o) <= order.indexOf(op)) return false;
    const st = stepOf(state, o)?.status;
    return st !== 'DONE' && st !== 'SKIPPED';
  }).map((o) => opStepLabel(o, t).toLowerCase());
  const after = remainingAfter.length
    ? t('now.after.then', { steps: remainingAfter.join(' → ') })
    : op === 'none'
      ? t('now.after.nothing')
      : t('now.after.last');

  if (contained || op === 'none') {
    return {
      op: 'none',
      eyebrow: t('now.eyebrow.contained', { step: stepCount, total: stepCount }),
      headline: t('now.contained.headline'),
      why: g
        ? t('now.contained.why', {
            travelled: g.travelled,
            waiting: g.ticketedNotDeparted,
            declined: g.declined,
          })
        : t('now.nothingPending'),
      facts: [
        [
          t('now.facts.hotelSpend'),
          formatPrice(state.kpis.totalPriceMinor, state.kpis.currency),
        ],
        [t('now.facts.confirmed'), String(state.implications.confirmedCount)],
      ],
      reversibility: 'done',
      reversibilityLine: t('now.contained.reversibility'),
      cta: t('now.contained.cta'),
      actionable: true,
      requiresSupervisor: false,
      after: t('now.contained.after'),
      mode: 'done',
    };
  }

  const eyebrow = `${t('now.eyebrow.base', { step: idx, total: stepCount, label: opStepLabel(op, t) })}${mode === 'running' ? t('now.eyebrow.running') : mode === 'retry' ? t('now.eyebrow.retry') : mode === 'wait' ? t('now.eyebrow.wait') : ''}`;
  let headline = next.label;
  let reversibility: Reversibility = 'reversible';
  let reversibilityLine = t('now.reversibility.default');
  const facts: Array<[string, string]> = [];
  // People first: every card says how many passengers the numbers stand for.
  if (state.kpis.affectedPassengers > 0)
    facts.push([
      t('now.facts.passengers'),
      `${state.kpis.affectedPassengers.toLocaleString()}${g && g.assignedPax > 0 ? t('now.facts.passengersWithFlight', { count: g.assignedPax.toLocaleString() }) : ''}`,
    ]);

  switch (op) {
    case 'rebooking':
      headline =
        mode === 'retry'
          ? t('now.rebooking.headlineRetry', { count: n })
          : t('now.rebooking.headline', { count: n });
      reversibilityLine = t('now.rebooking.reversibility');
      if (g) facts.push([t('now.facts.alreadyWithFlight'), `${g.assigned}/${g.rebookable}`]);
      if (g && g.noOptions > 0) facts.push([t('now.facts.noOptions'), String(g.noOptions)]);
      break;
    case 'tickets':
      if (automatic && b) {
        headline =
          b.issuing > 0
            ? t('now.tickets.issuing', { issued: b.issued, accepted: g?.accepted ?? 0 })
            : b.commitFailed > 0
              ? t('now.tickets.failedHeadline', { count: b.commitFailed })
              : t('now.tickets.waitingHeadline');
        reversibility = 'wait';
        reversibilityLine = t('now.tickets.automaticReversibility');
        facts.push(
          [t('now.facts.accepted'), String(g?.accepted ?? 0)],
          [t('now.facts.issued'), String(b.issued)],
        );
        if (b.commitFailed > 0) facts.push([t('now.facts.failed'), String(b.commitFailed)]);
      } else {
        headline = t('now.tickets.manualHeadline', { count: n });
        reversibility = 'irreversible';
        reversibilityLine = t('now.tickets.manualReversibility');
        if (g) facts.push([t('now.facts.withFlight'), String(g.assigned)]);
      }
      break;
    case 'hotels':
      headline = t('now.hotels.headline', { count: n });
      reversibilityLine = t('now.hotels.reversibility');
      facts.push([
        t('now.facts.estimatedPrice'),
        formatPrice(state.implications.estimatedPriceMinor, state.implications.currency),
      ]);
      if (g) facts.push([t('now.facts.alreadyOnHold'), String(g.hotelHeld)]);
      break;
    case 'vouchers':
      headline = t('now.vouchers.headline', { count: n });
      reversibilityLine = t('now.vouchers.reversibility');
      if (g && g.voucherNoKyc > 0) facts.push([t('now.facts.withoutKyc'), String(g.voucherNoKyc)]);
      break;
    case 'acceptance': {
      // QA fix 4 — the BFF flags the "ready, not sent, no run delivering them"
      // state as the `send-offers` repair: the card is ACTIONABLE (Send now)
      // instead of the disabled "Waiting for passengers".
      const sendRepair = step?.repair === 'send-offers' && !running;
      const sending = !sendRepair && mode === 'wait' && b && b.notSent > 0 && g && g.awaiting === 0;
      headline = sendRepair
        ? t('now.acceptance.headlineRepair', { count: n })
        : sending
          ? t('now.acceptance.headlineSending', { count: b.notSent })
          : mode === 'wait'
            ? t('now.acceptance.headlineWait')
            : t('now.acceptance.headline', { count: n });
      reversibility = sendRepair ? 'reversible' : mode === 'wait' ? 'wait' : 'irreversible';
      reversibilityLine = sendRepair
        ? t('now.acceptance.reversibilityRepair')
        : sending
          ? t('now.acceptance.reversibilitySending')
          : mode === 'wait'
            ? t('now.acceptance.reversibilityWait')
            : t('now.acceptance.reversibility');
      if (g)
        facts.push(
          [
            t('now.facts.accepted'),
            `${g.accepted}/${b ? Math.max(b.offerReady, b.offered) : g.atGate}`,
          ],
          [t('now.facts.declined'), String(g.declined)],
        );
      if (b && b.flightOnly > 0) facts.push([t('now.facts.flightOnly'), String(b.flightOnly)]);
      facts.push([
        t('now.facts.onHold'),
        formatPrice(state.kpis.heldPriceMinor, state.kpis.currency),
      ]);
      break;
    }
    case 'transfers': {
      const pax = tf?.paxToMove ?? n;
      const hotels = tf?.hotels ?? 0;
      const vehicles = tf?.vehiclesTotal ?? 0;
      headline =
        mode === 'retry' && tf && tf.noShow > 0
          ? t('now.transfers.headlineNoShow', {
              noShows: t('count.noShows', { count: tf.noShow }),
            })
          : mode === 'wait' && tf
            ? t('now.transfers.headlineWait', { arrived: tf.paxArrived, total: tf.paxToMove })
            : t('now.transfers.headline', {
                pax: pax.toLocaleString(),
                hotels: t('count.hotels', { count: hotels }),
                vehicles: t('count.vehicles', { count: vehicles }),
              });
      reversibility = mode === 'wait' ? 'wait' : 'reversible';
      reversibilityLine =
        mode === 'wait'
          ? t('now.transfers.reversibilityWait')
          : t('now.transfers.reversibility');
      if (tf) {
        facts.push(
          [
            t('now.facts.atTheHotel'),
            `${tf.paxArrived.toLocaleString()} / ${tf.paxToMove.toLocaleString()}`,
          ],
          [
            t('now.facts.onTheRoad'),
            t('now.facts.onTheRoadValue', {
              pax: tf.paxOnRoad.toLocaleString(),
              vehicles: t('count.vehicles', { count: tf.vehiclesOnRoad }),
            }),
          ],
          [
            t('now.facts.vehiclesPlanned'),
            tf.vehiclesOnRoad > 0
              ? t('now.facts.vehiclesPlannedValue', {
                  total: tf.vehiclesTotal,
                  pending: tf.vehiclesPlanned,
                })
              : String(tf.vehiclesTotal),
          ],
          [
            t('now.facts.returnTrips'),
            tf.returnScheduled + tf.returnOnRoad + tf.returnDone > 0
              ? t('now.facts.returnTripsValue', {
                  done: tf.returnDone,
                  onRoad: tf.returnOnRoad,
                  scheduled: tf.returnScheduled,
                })
              : t('now.facts.returnTripsScheduled'),
          ],
        );
        if (tf.noShow > 0) facts.push([t('now.facts.noShow'), String(tf.noShow)]);
        if (tf.failed > 0) facts.push([t('now.facts.vendorFailed'), String(tf.failed)]);
      }
      break;
    }
    case 'contain': {
      const cm = step?.containMode;
      // Commits in flight (accepted, ticket + hotel being issued): containment
      // waits for them — forcing would tear down what the passenger just accepted.
      const commitInFlight = step?.status === 'BLOCKED' && (b?.issuing ?? 0) > 0;
      headline = commitInFlight
        ? t('now.contain.headlineCommit')
        : cm === 'NATURAL'
          ? t('now.contain.headlineNatural')
          : cm === 'TRAVELLING'
            ? t('now.contain.headlineTravelling')
            : t('now.contain.headlineForced');
      reversibility = commitInFlight
        ? 'wait'
        : cm === 'FORCED'
          ? 'destructive'
          : cm === 'TRAVELLING'
            ? 'wait'
            : 'irreversible';
      reversibilityLine = commitInFlight
        ? t('now.contain.reversibilityCommit', { count: b?.issuing ?? 0 })
        : cm === 'FORCED'
          ? t('now.contain.reversibilityForced', {
              holds: g?.hotelHeld ?? 0,
              selections: g?.assigned ?? 0,
            })
          : cm === 'TRAVELLING'
            ? t('now.contain.reversibilityTravelling')
            : t('now.contain.reversibilityNatural');
      if (g)
        facts.push(
          [t('now.facts.travelled'), String(g.travelled)],
          [t('now.facts.flightPending'), String(g.ticketedNotDeparted)],
        );
      break;
    }
  }
  if (mode === 'running' && activeRun) {
    reversibility = 'wait';
    reversibilityLine = t('now.reversibility.running', {
      operator: activeRun.operatorUrn ? shortOperator(activeRun.operatorUrn, t) : t('common:holds.lock.anotherOperator'),
    });
  }

  const actionable =
    (next.actionable ?? true) &&
    !automatic &&
    !running &&
    mode !== 'wait' &&
    mode !== 'blocked' &&
    mode !== 'unknown';
  // The CTA is composed HERE from op + mode + count — the BFF's `cta`/`reason`
  // strings are Spanish today (until `ctaCode`/`reasonCode` land, phase 1 BFF).
  const cta = running
    ? runningCta(activeRun?.op ?? '', activeRun?.progress, live, t)
    : automatic
      ? t('now.automatic.cta')
      : ctaFor(op, mode, n, step, tf, t);
  return {
    op,
    eyebrow,
    headline,
    why: automatic ? t('now.automatic.why') : whyFor(op, mode, n, step, g, b, tf, t),
    facts,
    reversibility,
    reversibilityLine,
    cta,
    actionable,
    requiresSupervisor:
      next.requiresRole === 'ops_supervisor' || step?.requiresRole === 'ops_supervisor',
    after,
    mode,
    secondary:
      op === 'transfers' && !running
        ? { label: t('now.secondary.reviewPlan'), target: { view: 'hotels', transfer: 'PLAN' } }
        : undefined,
    // QA fix 4 — the primary button fires the send-offers REPAIR, not resolve-next.
    action: op === 'acceptance' && step?.repair === 'send-offers' && !running ? 'send-offers' : undefined,
  };
}

function ctaFor(
  op: ResolutionOp,
  mode: NowCard['mode'],
  n: number,
  step: ResolutionStep | undefined,
  tf: TransferFacts | null = null,
  t: TowerT = towerT,
): string {
  switch (op) {
    case 'transfers': {
      if (mode === 'retry' && tf && tf.noShow > 0)
        return t('cta.transfers.redispatch', { noShows: t('count.noShows', { count: tf.noShow }) });
      if (mode === 'wait' && tf)
        return t('cta.transfers.wait', { arrived: tf.paxArrived, total: tf.paxToMove });
      const pax = tf?.paxToMove ?? n;
      const hotels = tf?.hotels ?? 0;
      const vehicles = tf?.vehiclesTotal ?? 0;
      const args = {
        pax: pax.toLocaleString(),
        hotels: t('count.hotels', { count: hotels }),
        vehicles: t('count.vehicles', { count: vehicles }),
      };
      return vehicles > 0 ? t('cta.transfers.sendVehicles', args) : t('cta.transfers.send', args);
    }
    case 'rebooking':
      return mode === 'retry'
        ? t('cta.rebooking.retry', { count: n })
        : t('cta.rebooking.start', { count: n });
    case 'tickets':
      return t('cta.tickets', { count: n });
    case 'hotels':
      return t('cta.hotels', { count: n });
    case 'vouchers':
      return t('cta.vouchers', { count: n });
    case 'acceptance':
      // QA fix 4 — the stuck "ready, not sent" state gets its repair verb.
      if (step?.repair === 'send-offers') return t('cta.acceptance.send', { count: n });
      return mode === 'wait' ? t('cta.acceptance.wait') : t('cta.acceptance.force', { count: n });
    case 'contain':
      return step?.containMode === 'FORCED'
        ? t('cta.contain.force')
        : step?.containMode === 'TRAVELLING'
          ? t('cta.contain.travelling')
          : t('cta.contain.now');
    default:
      return t('cta.continue');
  }
}

function whyFor(
  op: ResolutionOp,
  mode: NowCard['mode'],
  n: number,
  step: ResolutionStep | undefined,
  g: DisruptionGroupFacts | null,
  b: BundleFacts | null = null,
  tf: TransferFacts | null = null,
  t: TowerT = towerT,
): string {
  if (mode === 'unknown') return t('why.unknown');
  if (mode === 'running') return t('why.running');
  switch (op) {
    case 'rebooking':
      return mode === 'retry'
        ? t('why.rebooking.retry', {
            count: n,
            suffix:
              g && g.noOptions > 0
                ? t('why.rebooking.retryNoOptions', { count: g.noOptions })
                : '',
          })
        : t('why.rebooking.start', { count: n });
    case 'tickets':
      return t('why.tickets', { count: n });
    case 'hotels':
      return t('why.hotels', { count: n });
    case 'vouchers':
      return t('why.vouchers', { count: n });
    case 'acceptance':
      // QA fix 4 — stuck-complete offers with no run delivering them.
      if (step?.repair === 'send-offers') return t('why.acceptance.repair', { count: n });
      return mode === 'wait'
        ? b && b.notSent > 0 && (g?.awaiting ?? 0) === 0
          ? t('why.acceptance.sending')
          : t('why.acceptance.wait')
        : t('why.acceptance.start', { count: n });
    case 'transfers': {
      if (mode === 'wait')
        return t('why.transfers.wait', {
          count: tf?.vehiclesOnRoad ?? 0,
          pickup: tf?.nextPickupAt
            ? t('why.transfers.nextPickup', { time: shortTime(tf.nextPickupAt) })
            : '',
        });
      if (mode === 'retry') return t('why.transfers.retry', { count: tf?.noShow ?? 0 });
      const pax = tf?.paxToMove ?? n;
      const hotels = tf?.hotels ?? 0;
      return t('why.transfers.start', {
        pax: pax.toLocaleString(),
        across: hotels > 0 ? t('why.transfers.across', { count: hotels }) : '',
      });
    }
    case 'contain':
      if (step?.status === 'BLOCKED' && (b?.issuing ?? 0) > 0)
        return t('why.contain.commit', { count: b?.issuing ?? 0 });
      return step?.containMode === 'NATURAL'
        ? t('why.contain.natural')
        : step?.containMode === 'TRAVELLING'
          ? t('why.contain.travelling', {
              pending: g
                ? t('why.contain.travellingPending', {
                    count: Math.max(
                      0,
                      g.rebookable - (g.issued ?? g.ticketed) - g.declined - g.notApplicable,
                    ),
                  })
                : '',
            })
          : t('why.contain.forced');
    default:
      return '';
  }
}

export function shortOperator(operatorUrn: string, t: TowerT = towerT): string {
  const tail = operatorUrn.split(':').pop() ?? operatorUrn;
  const name = tail.includes('|') ? (tail.split('|').pop() ?? tail) : tail;
  return name.trim() || t('common:holds.lock.anotherOperator');
}

export function runningCta(
  op: string,
  progress: { done: number; total: number; unit: string } | undefined,
  live: DisruptionProgress | undefined,
  t: TowerT = towerT,
): string {
  switch (op) {
    case 'rebooking': {
      const done = live?.processedGroups ?? 0;
      const total = live?.plannedGroups ?? 0;
      return total > 0
        ? t('runningCta.rebooking', { done, total })
        : t('runningCta.rebookingPlain');
    }
    case 'allocation': {
      const a = live?.allocationRun;
      return a && a.totalGroups > 0
        ? t('runningCta.allocation', {
            done: a.heldGroups + a.alreadyReservedGroups,
            total: a.totalGroups,
          })
        : t('runningCta.allocationPlain');
    }
    case 'tickets':
      return progress
        ? t('runningCta.tickets', { done: progress.done, total: progress.total })
        : t('runningCta.ticketsPlain');
    case 'acceptance':
      return progress
        ? t('runningCta.acceptance', { done: progress.done, total: progress.total })
        : t('runningCta.acceptancePlain');
    case 'vouchers':
      return t('runningCta.vouchers');
    case 'transfers': {
      const tr = live?.transferRun;
      if (progress && progress.total > 0)
        return t('runningCta.transfers', { done: progress.done, total: progress.total });
      if (tr && tr.vehiclesPlanned + tr.vehiclesOnRoad + tr.arrivedRuns > 0)
        return t('runningCta.transfers', {
          done: tr.vehiclesOnRoad + tr.arrivedRuns,
          total: tr.vehiclesPlanned + tr.vehiclesOnRoad + tr.arrivedRuns,
        });
      return t('runningCta.transfersPlain');
    }
    default:
      return t('runningCta.default');
  }
}

/* ── IN PROGRESS ──────────────────────────────────────────────────────────────── */

export interface RunSample {
  at: number;
  done: number;
}

export interface InProgress {
  active: boolean;
  op?: string | undefined;
  title: string;
  operator?: string | undefined;
  startedAt?: string | undefined;
  done?: number | undefined;
  total?: number | undefined;
  pct?: number | undefined;
  ratePerMin?: number | undefined;
  etaMs?: number | undefined;
  vendors: string[];
  stalledForMs?: number | undefined;
  /** When idle: one line about the LAST run. */
  idleLine: string;
  dialogOp: 'rebooking' | 'allocation' | null;
  /** A fire in flight with no run marker yet — draw an indeterminate bar. */
  indeterminate?: boolean | undefined;
  note?: string | undefined;
  /** A Tower view to open for the details (the shuttle board lives in Hotels). */
  link?: { label: string; target: TowerTarget } | undefined;
}

export function buildInProgress(
  state: ResolutionState,
  live: DisruptionProgress | undefined,
  samples: RunSample[],
  now: number,
  pending: { op: ResolutionOp; since: number; count: number } | null = null,
  t: TowerT = towerT,
): InProgress {
  const activeRun = activeRunOf(state, live);
  const vendors = [
    ...(live?.delayedBy ?? []).map((v) => t('progress.vendor.limited', { vendor: vendorLabel(v, t) })),
    ...(live?.slowBy ?? []).map((v) => t('progress.vendor.slow', { vendor: vendorLabel(v, t) })),
  ];
  if (!activeRun && pending) {
    // The POST is in flight and no run marker has been pushed yet — the airline /
    // booking is working on it. Indeterminate, but visibly alive.
    const flights = state.kpis.caseCount;
    return {
      active: true,
      op: pending.op,
      title: t(`opTitle.${pending.op}`, { defaultValue: pending.op }),
      operator: t('operator.you'),
      startedAt: new Date(pending.since).toISOString(),
      done: 0,
      total: 0,
      pct: undefined,
      vendors,
      idleLine: '',
      dialogOp: null,
      indeterminate: true,
      note:
        pending.op === 'tickets'
          ? t('progress.note.tickets', {
              count: pending.count,
              flights: t('count.flights', { count: flights }),
            })
          : pending.op === 'hotels'
            ? t('progress.note.hotels', { count: pending.count })
            : pending.op === 'transfers'
              ? t('progress.note.transfers', { count: pending.count })
              : t('progress.note.generic', { count: pending.count }),
    };
  }
  // The AUTOMATIC commit in flight: once an offer is accepted, the ticket is
  // issued and the hotel confirmed by the system — no run marker, but a real,
  // countable progress (issued / accepted) that used to be invisible.
  const g = liveFacts(state, live);
  const tf = transfersOf(state, live);
  if (!activeRun && g) {
    const b = bundleOf(g);
    if (b.issuing > 0) {
      const failed = b.commitFailed;
      return {
        active: true,
        op: 'issue',
        title: t('progress.issue.title'),
        operator: t('operator.system'),
        startedAt: undefined,
        done: b.issued,
        total: g.accepted,
        pct: g.accepted > 0 ? (b.issued / g.accepted) * 100 : 0,
        vendors,
        idleLine: '',
        dialogOp: null,
        note: `${t('progress.issue.note', { count: b.issuing - failed })}${failed > 0 ? t('progress.issue.noteStale', { count: failed }) : ''}`,
      };
    }
    // Shuttles on the road (MASS ground transport): boarding / departures /
    // arrivals arrive from the vendor run by run — a countable automatic phase.
    if (tf && tf.vehiclesOnRoad > 0) {
      return {
        active: true,
        op: 'transfers',
        title: t('progress.shuttles.title'),
        operator: t('operator.system'),
        startedAt: undefined,
        done: tf.paxArrived,
        total: tf.paxToMove,
        pct: tf.paxToMove > 0 ? (tf.paxArrived / tf.paxToMove) * 100 : 0,
        vendors,
        idleLine: '',
        dialogOp: null,
        note: `${t('progress.shuttles.note', { count: tf.vehiclesOnRoad })}${tf.nextPickupAt ? t('progress.shuttles.notePickup', { time: shortTime(tf.nextPickupAt) }) : ''}${tf.noShow > 0 ? t('progress.shuttles.noteNoShow', { count: tf.noShow }) : ''}`,
        link: {
          label: t('progress.link.shuttleBoard'),
          target: { view: 'hotels', transfer: 'EN_ROUTE' },
        },
      };
    }
    // Return runs (hotel → airport) in flight.
    if (tf && tf.returnOnRoad > 0) {
      const total = tf.returnScheduled + tf.returnOnRoad + tf.returnDone;
      return {
        active: true,
        op: 'return-transfers',
        title: t('progress.return.title'),
        operator: t('operator.system'),
        startedAt: undefined,
        done: tf.returnDone,
        total,
        pct: total > 0 ? (tf.returnDone / total) * 100 : 0,
        vendors,
        idleLine: '',
        dialogOp: null,
        note: t('progress.return.note', {
          onRoad: tf.returnOnRoad,
          scheduled: tf.returnScheduled,
        }),
        link: {
          label: t('progress.link.shuttleBoard'),
          target: { view: 'hotels', transfer: 'RETURN' },
        },
      };
    }
    // Offers out, passengers deciding: the wait IS the work in progress.
    if (g.awaiting > 0) {
      const total = Math.max(b.offerReady, b.offered);
      return {
        active: true,
        op: 'acceptance-wait',
        title: t('progress.acceptance.title'),
        operator: undefined,
        startedAt: undefined,
        done: g.accepted,
        total,
        pct: total > 0 ? (g.accepted / total) * 100 : 0,
        vendors,
        idleLine: '',
        dialogOp: null,
        note: `${t('progress.acceptance.note', { accepted: g.accepted, declined: g.declined, awaiting: g.awaiting })}${b.notSent > 0 ? t('progress.acceptance.noteNotSent', { count: b.notSent }) : ''}`,
      };
    }
  }
  if (!activeRun) {
    const rs = live?.runStatus ?? state.progress.runStatus;
    const done = live?.processedGroups ?? state.progress.processedGroups ?? 0;
    const total = live?.plannedGroups ?? state.progress.plannedGroups ?? 0;
    const alloc = live?.allocationRun;
    const parts: string[] = [];
    if (rs && rs !== 'NONE') {
      // QA fix 5 — run recency: age the LAST run instead of rendering a weeks-old
      // search as fresh. Older than 24h = stale ("needs a fresh search"); a marker
      // without the timestamp (older rebooking) keeps the legacy line unchanged.
      const completedAt = live?.runCompletedAt ?? state.progress.lastRunCompletedAt;
      const ageMs = completedAt ? now - new Date(completedAt).getTime() : Number.NaN;
      const aged = Number.isFinite(ageMs) && ageMs >= 0;
      const stale = aged && ageMs > RUN_STALE_AFTER_MS;
      parts.push(
        `${t('progress.idle.flightSearch', { status: runStatusLabel(rs, t) })}${total ? ` ${done}/${total}` : ''}` +
          (aged ? ` · ${ageLabel(ageMs, t)}` : '') +
          (stale ? t('progress.idle.stale') : t('progress.idle.seatsOnHold')),
      );
    }
    if (alloc && alloc.status !== 'RUNNING')
      parts.push(
        t('progress.idle.hotels', {
          status: runStatusLabel(alloc.status, t),
          done: alloc.heldGroups + alloc.alreadyReservedGroups,
          total: alloc.totalGroups,
        }),
      );
    return {
      active: false,
      title: t('progress.idle.title'),
      vendors,
      idleLine: parts.length
        ? t('progress.idle.last', { parts: parts.join(' · ') })
        : t('progress.idle.never'),
      dialogOp: rs && rs !== 'NONE' ? 'rebooking' : alloc ? 'allocation' : null,
    };
  }
  let done = activeRun.progress?.done ?? 0;
  let total = activeRun.progress?.total ?? 0;
  if (activeRun.op === 'rebooking') {
    done = live?.processedGroups ?? 0;
    total = live?.plannedGroups ?? 0;
  } else if (activeRun.op === 'allocation' && live?.allocationRun) {
    done = live.allocationRun.heldGroups + live.allocationRun.alreadyReservedGroups;
    total = live.allocationRun.totalGroups;
  } else if (activeRun.op === 'transfers' && !activeRun.progress && tf) {
    done = tf.vehiclesOnRoad + (live?.transferRun?.arrivedRuns ?? 0);
    total = tf.vehiclesTotal;
  }
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  // Client-side rate from the last samples (BFF exposes no rate/eta yet).
  let ratePerMin: number | undefined;
  let etaMs: number | undefined;
  if (samples.length >= 2) {
    const a = samples[0]!;
    const b = samples[samples.length - 1]!;
    const dt = b.at - a.at;
    const dd = b.done - a.done;
    if (dt > 3000 && dd > 0) {
      ratePerMin = (dd / dt) * 60_000;
      if (total > done) etaMs = ((total - done) / dd) * dt;
    }
  }
  const last = samples.length ? samples[samples.length - 1] : null;
  const stalledForMs = last && now - last.at > 120_000 && done < total ? now - last.at : undefined;
  return {
    active: true,
    op: activeRun.op,
    title: t(`opTitle.${activeRun.op}`, { defaultValue: activeRun.op }),
    operator: shortOperator(activeRun.operatorUrn, t),
    startedAt: activeRun.startedAt,
    done,
    total,
    pct,
    ratePerMin,
    etaMs,
    vendors,
    stalledForMs,
    idleLine: '',
    dialogOp:
      activeRun.op === 'rebooking'
        ? 'rebooking'
        : activeRun.op === 'allocation'
          ? 'allocation'
          : null,
    link:
      activeRun.op === 'transfers'
        ? {
            label: t('progress.link.shuttleBoard'),
            target: { view: 'hotels', transfer: 'PLAN' },
          }
        : undefined,
  };
}

/** QA fix 5 — a terminal rebooking run older than this is STALE: its options may
 *  name flights that already departed, so the idle line says "needs a fresh search". */
export const RUN_STALE_AFTER_MS = 24 * 3_600_000;

/** Compact age for the idle line — "just now" / "12 min ago" / "5 h ago" / "3 d ago" / "6w ago". */
export function ageLabel(ms: number, t: TowerT = towerT): string {
  if (ms < 60_000) return t('age.justNow');
  const m = Math.floor(ms / 60_000);
  if (m < 60) return t('age.minutes', { count: m });
  const h = Math.floor(m / 60);
  if (h < 48) return t('age.hours', { count: h });
  const d = Math.floor(h / 24);
  if (d < 14) return t('age.days', { count: d });
  return t('age.weeks', { count: Math.floor(d / 7) });
}

export function runStatusLabel(rs: string, t: TowerT = towerT): string {
  return t(`runStatus.${rs}`, { defaultValue: rs.toLowerCase() });
}

/* ── ATTENTION ──────────────────────────────────────────────────────────────── */

export interface AttentionRow {
  key: string;
  n: number;
  label: string;
  detail?: string | undefined;
  tone: 'warn' | 'danger' | 'info';
  verb: string;
  target: TowerTarget | null;
  /** When the row's verb is the NOW action itself (e.g. retry). */
  primary?: boolean | undefined;
  /** A dedicated action behind the verb (not the NOW step, not a view). */
  action?: 'repair-issue' | 'redispatch-transfers' | 'send-offers' | undefined;
  /** For `primary` rows: the op to fire explicitly (may differ from the recommended
   *  step — the BFF honours an actionable `expectedOp`). */
  op?: ResolutionOp | undefined;
}

export function buildAttention(
  state: ResolutionState,
  live: DisruptionProgress | undefined,
  t: TowerT = towerT,
): AttentionRow[] {
  const p = state.problems;
  const g = liveFacts(state, live);
  const b = g ? bundleOf(g) : null;
  const rows: AttentionRow[] = [];
  // The facts count is the truth for "no options": the reason breakdown also
  // counts NOT_APPLICABLE groups (POLICY_DISABLED), which the NO_OPTIONS roster
  // filter would never list — those get their own row below.
  const byReasonTotal = Object.values(p.noOptionsByReason ?? {}).reduce((a, b) => a + b, 0);
  const noOptions = g ? g.noOptions : byReasonTotal;
  const policyOff = p.noOptionsByReason?.POLICY_DISABLED ?? 0;
  const rebook = stepOf(state, 'rebooking');
  const runDone =
    (live?.runStatus ?? state.progress.runStatus) === 'COMPLETED' ||
    (live?.runStatus ?? state.progress.runStatus) === 'PARTIAL';
  const pendingAfterRun =
    g && runDone && !live?.activeRun
      ? Math.max(0, g.rebookable - g.assigned - g.noOptions - g.optionsReady)
      : 0;

  if (pendingAfterRun > 0 && rebook?.status !== 'RUNNING') {
    rows.push({
      key: 'pending-after-run',
      n: pendingAfterRun,
      label: t('attention.pendingAfterRun.label'),
      detail: t('attention.pendingAfterRun.detail'),
      tone: 'warn',
      verb: t('attention.verb.retry'),
      target: null,
      primary: true,
      op: 'rebooking',
    });
  }
  if (noOptions > 0) {
    // Reasons shown only for the groups that really are NO_OPTIONS.
    const reasons = { ...(p.noOptionsByReason ?? {}) };
    if (g && g.notApplicable > 0) delete reasons.POLICY_DISABLED;
    rows.push({
      key: 'noopt',
      n: noOptions,
      label: t('attention.noOptions.label'),
      detail: noOptionsSub(reasons, t),
      tone: 'warn',
      verb: t('attention.verb.view'),
      target: { view: 'passengers', hint: 'NO_OPTIONS' },
    });
  }
  if (g && g.notApplicable > 0 && policyOff > 0) {
    rows.push({
      key: 'na-policy',
      n: g.notApplicable,
      label: t('attention.naPolicy.label'),
      detail: t('attention.naPolicy.detail'),
      tone: 'info',
      verb: t('attention.verb.view'),
      target: { view: 'passengers', hint: 'NOT_APPLICABLE' },
    });
  }
  // Options generated but no seat could be taken automatically (a spill the
  // auto-select could not place): manual work per PNR — the flow does not wait.
  if (g && g.optionsReady > 0 && !live?.activeRun) {
    rows.push({
      key: 'options-not-chosen',
      n: g.optionsReady,
      label: t('attention.optionsNotChosen.label'),
      detail: t('attention.optionsNotChosen.detail'),
      tone: 'warn',
      verb: t('attention.verb.view'),
      target: { view: 'passengers', hint: 'OPTIONS_READY' },
    });
  }
  if (p.degradedGroups > 0) {
    rows.push({
      key: 'degraded',
      n: p.degradedGroups,
      label: t('attention.degraded.label'),
      detail: t('attention.degraded.detail'),
      tone: 'warn',
      verb: t('attention.verb.retry'),
      target: null,
      primary: true,
      op: 'rebooking',
    });
  }
  // Tickets issued but no room yet — irreversible commitment made, passenger not
  // sheltered. Alarm, with the NOW action as the verb when hotels are the next step.
  if (g && g.ticketed > 0 && g.hotelNeeded > 0 && (!b || g.ticketed > b.issued)) {
    const covered = g.hotelHeld + g.hotelConfirmed;
    const open = Math.max(0, g.hotelNeeded - covered - p.allocationGaps);
    if (open > 0) {
      const nextIsHotels = state.nextStep.op === 'hotels';
      rows.push({
        key: 'ticketed-no-hotel',
        n: open,
        label: t('attention.ticketedNoHotel.label'),
        detail: t('attention.ticketedNoHotel.detail'),
        tone: 'warn',
        verb: nextIsHotels ? t('attention.verb.bookHotels') : t('attention.verb.view'),
        target: nextIsHotels ? null : { view: 'hotels' },
        primary: nextIsHotels,
        op: nextIsHotels ? 'hotels' : undefined,
      });
    }
  }
  // Acceptance bundle: offer-ready groups whose offer has not gone out — the
  // system sends it by itself; if it lingers, notifications need a look.
  const acceptanceStep = stepOf(state, 'acceptance');
  const acceptanceOpen =
    !acceptanceStep || (acceptanceStep.status !== 'BLOCKED' && acceptanceStep.status !== 'SKIPPED');
  // Only when the BFF speaks the bundle (against an older BFF "not sent" is a
  // derived guess, drawn as a pending pool but never an alarm).
  const notSent = Math.max(p.offersNotSent ?? 0, b && b.fromBff && acceptanceOpen ? b.notSent : 0);
  if (notSent > 0 && acceptanceOpen) {
    // QA fix 4 — with NO run in flight nothing will ever deliver these (the gate
    // is edge-triggered): the row's verb IS the repair, not a view. While a run
    // is live (or the acceptance step itself runs) the automatic send may still
    // land — keep the passive "View".
    const idle = !activeRunOf(state, live) && acceptanceStep?.status !== 'RUNNING';
    rows.push({
      key: 'notsent',
      n: notSent,
      label: t('attention.notSent.label'),
      detail: idle ? t('attention.notSent.detailIdle') : t('attention.notSent.detail'),
      tone: 'warn',
      verb: idle ? t('attention.verb.sendNow') : t('attention.verb.view'),
      target: { view: 'passengers', hint: 'SELECTED' },
      ...(idle ? { action: 'send-offers' as const } : {}),
    });
  }
  const commitFailed = Math.max(p.commitFailed ?? 0, b?.commitFailed ?? 0);
  if (commitFailed > 0) {
    rows.push({
      key: 'commit-failed',
      n: commitFailed,
      label: t('attention.commitFailed.label'),
      detail: t('attention.commitFailed.detail'),
      tone: 'danger',
      verb: t('attention.verb.repair'),
      target: { view: 'passengers' },
      action: 'repair-issue',
    });
  }
  if (p.allocationGaps > 0) {
    rows.push({
      key: 'gaps',
      n: p.allocationGaps,
      label: t('attention.gaps.label'),
      detail: t('attention.gaps.detail'),
      tone: 'warn',
      verb: t('attention.verb.pickManually'),
      target: { view: 'hotels' },
    });
  }
  if ((p.declined ?? 0) > 0) {
    rows.push({
      key: 'declined',
      n: p.declined ?? 0,
      label: t('attention.declined.label'),
      detail: t('attention.declined.detail'),
      tone: 'danger',
      verb: t('attention.verb.view'),
      target: { view: 'passengers' },
    });
  }
  if ((g?.voucherNoKyc ?? 0) > 0) {
    rows.push({
      key: 'nokyc',
      n: g?.voucherNoKyc ?? 0,
      label: t('attention.noKyc.label'),
      detail: t('attention.noKyc.detail'),
      tone: 'info',
      verb: t('attention.verb.view'),
      target: { view: 'passengers' },
    });
  }
  if ((p.holdsWithoutOffer ?? 0) > 0) {
    rows.push({
      key: 'nooffer',
      n: p.holdsWithoutOffer ?? 0,
      label: t('attention.noOffer.label'),
      tone: 'info',
      verb: t('attention.verb.view'),
      target: { view: 'passengers' },
    });
  }
  if (p.awaitingAcceptance > 0) {
    rows.push({
      key: 'awaiting',
      n: p.awaitingAcceptance,
      label: t('attention.awaiting.label'),
      detail: t('attention.awaiting.detail'),
      tone: 'info',
      verb: t('attention.verb.view'),
      target: { view: 'passengers' },
    });
  }
  // MASS ground transport: passengers left at a pickup, vendor-failed runs, groups
  // ticketed + hotel with no seat after the BFF threshold, unscheduled returns.
  const tf = transfersOf(state, live);
  const nextIsTransfers = state.nextStep.op === 'transfers';
  const noShow = Math.max(p.transferNoShow ?? 0, tf?.noShow ?? 0);
  if (noShow > 0) {
    rows.push({
      key: 'transfer-noshow',
      n: noShow,
      label: t('attention.transferNoShow.label'),
      detail: t('attention.transferNoShow.detail'),
      tone: 'danger',
      verb: t('attention.verb.redispatch'),
      target: { view: 'hotels', transfer: 'NO_SHOW' },
      action: 'redispatch-transfers',
    });
  }
  const failedRuns = Math.max(p.transferFailed ?? 0, tf?.failed ?? 0);
  if (failedRuns > 0) {
    rows.push({
      key: 'transfer-failed',
      n: failedRuns,
      label: t('attention.transferFailed.label'),
      detail: t('attention.transferFailed.detail'),
      tone: 'danger',
      verb: t('attention.verb.view'),
      target: { view: 'hotels', transfer: 'FAILED' },
    });
  }
  const late = p.transferLate ?? 0;
  if (late > 0) {
    rows.push({
      key: 'transfer-late',
      n: late,
      label: t('attention.transferLate.label'),
      detail: t('attention.transferLate.detail'),
      tone: 'warn',
      verb: nextIsTransfers ? t('attention.verb.sendToHotels') : t('attention.verb.view'),
      target: nextIsTransfers ? null : { view: 'hotels', transfer: 'WAITING' },
      primary: nextIsTransfers,
    });
  }
  const returnUnscheduled = p.returnUnscheduled ?? 0;
  if (returnUnscheduled > 0) {
    rows.push({
      key: 'return-unscheduled',
      n: returnUnscheduled,
      label: t('attention.returnUnscheduled.label', { count: returnUnscheduled }),
      detail: t('attention.returnUnscheduled.detail'),
      tone: 'warn',
      verb: t('attention.verb.view'),
      target: { view: 'hotels', transfer: 'RETURN' },
    });
  }
  const vendors = [...(p.delayedBy ?? []), ...(live?.delayedBy ?? [])];
  const uniq = Array.from(new Set(vendors));
  if (uniq.length) {
    rows.push({
      key: 'vendors',
      n: uniq.length,
      label: t('attention.vendors.label', { count: uniq.length }),
      detail: uniq.map((v) => vendorLabel(v, t)).join(' · '),
      tone: 'info',
      verb: t('attention.verb.providers'),
      target: null,
    });
  }
  return rows;
}

/* ── flight strips ─────────────────────────────────────────────────────────── */

export interface FlightStrip {
  caseUrn: string;
  flightUrn?: string | undefined;
  code: string;
  route: string;
  time: string;
  pax: number;
  status: string;
  departed: boolean;
  /** The flight's hotel spend at the airline's PRICE (`airline-money@v2`); null = not available. */
  priceMinor?: number | null | undefined;
  /** Per-flight facts (client-side roster fold; phase 2 = BFF `perFlight[]`). */
  facts?: FlightFacts | undefined;
}

export function buildStrips(
  detail: DisruptionDetail | undefined,
  now: number,
  priceByCase?: Map<string, number | null>,
  perFlight?: PerFlightFacts,
): FlightStrip[] {
  const cases = detail?.cases ?? [];
  const rows = cases.map((c: DisruptionCaseRow): FlightStrip => {
    // Issue #662: Format in departure airport timezone
    const time = formatFlightClock24(c.departureAt, c.origin);
    const depMs = c.departureAt ? Date.parse(c.departureAt) : NaN;
    return {
      caseUrn: c.caseUrn,
      flightUrn: c.flightUrn,
      code: c.flightNumber ?? c.airportCode,
      route: c.origin && c.destination ? `${c.origin}→${c.destination}` : c.airportCode,
      time,
      pax: c.passengerCount,
      status: c.status,
      departed: Number.isFinite(depMs) && depMs < now,
      priceMinor: priceByCase?.get(c.caseUrn),
      facts: perFlight
        ? (perFlight.byFlight.get(c.flightUrn ?? '') ??
          [...perFlight.byFlight.values()].find((f) => f.caseUrn === c.caseUrn))
        : undefined,
    };
  });
  // Most work first: not departed, then most groups still without a flight, then
  // more pax; departed sink to the bottom.
  rows.sort((a, b) => {
    if (a.departed !== b.departed) return a.departed ? 1 : -1;
    const oa = a.facts ? a.facts.pending + a.facts.noOptions + a.facts.optionsReady : -1;
    const ob = b.facts ? b.facts.pending + b.facts.noOptions + b.facts.optionsReady : -1;
    if (oa !== ob) return ob - oa;
    return b.pax - a.pax;
  });
  return rows;
}

/* ── misc ──────────────────────────────────────────────────────────────────── */

export function elapsedLabel(
  fromIso: string | undefined,
  now: number,
  t: TowerT = towerT,
): string {
  if (!fromIso) return '—';
  const from = new Date(fromIso).getTime();
  if (Number.isNaN(from)) return '—';
  const ms = Math.max(0, now - from);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0
    ? t('duration.hoursMinutes', { hours: h, minutes: String(m).padStart(2, '0') })
    : t('duration.minutes', { count: m });
}

export function shortDuration(ms: number, t: TowerT = towerT): string {
  if (ms < 60_000) return t('duration.seconds', { count: Math.max(1, Math.round(ms / 1000)) });
  const m = Math.round(ms / 60_000);
  if (m < 60) return t('duration.minutes', { count: m });
  const h = Math.floor(m / 60);
  return t('duration.hoursShort', { hours: h, minutes: String(m % 60).padStart(2, '0') });
}
